import { translate } from '@teamleader/i18n';
import debounce from 'lodash/debounce';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import * as styles from './styles.css';
import Box from '../Box';
import FormItem from '../FormItem';
import FormLabel from '../FormLabel';
import Input from '../Input';
import Link from '../Link';

interface TextSignatureProps {
  onChange?: (signature: string) => void;
  handleReset?: () => void;
  width?: number;
  height?: number;
}

const FONT_SIZE = 24;
const MIN_FONT_SIZE = 14;
const PADDING = 12;

const TextSignature: React.FC<TextSignatureProps> = ({ onChange, handleReset, width, height = 120 }) => {
  const ref = useRef<HTMLCanvasElement>(null);
  const [name, setName] = useState('');
  const [fontSize, setFontSize] = useState(FONT_SIZE);
  const [warning, setWarning] = useState('');
  const [computedWidth, setComputedWidth] = useState(263);
  const canvasWidth = width || computedWidth;

  const handleChange = useRef(
    debounce((name: string) => {
      if (onChange) {
        onChange(name.trim() === '' ? '' : ref.current!.toDataURL());
      }
    }, 200),
  ).current;

  const onNameChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setName(event.target.value);
      setWarning('');
      handleChange(event.target.value);
    },
    [handleChange],
  );

  const computeWidth = useCallback((node?: HTMLElement) => {
    if (!node || node.clientWidth === 0) {
      return;
    }

    setComputedWidth(node.clientWidth);
  }, []);

  useEffect(() => {
    const ctx = ref.current!.getContext('2d')!;
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
  }, [ref]);

  useEffect(() => {
    const ctx = ref.current!.getContext('2d')!;

    ctx.font = `${FONT_SIZE}px Dancing Script, cursive`;
    const { width: textWidth, actualBoundingBoxAscent = 15, actualBoundingBoxDescent = 2 } = ctx.measureText(name);
    const textHeight = actualBoundingBoxAscent + actualBoundingBoxDescent + PADDING * 2;

    ref.current!.height = textHeight;
    ctx.clearRect(0, 0, canvasWidth, textHeight);

    const fontSize = Math.min(FONT_SIZE, (FONT_SIZE * (canvasWidth - PADDING)) / textWidth);

    if (fontSize < MIN_FONT_SIZE) {
      setWarning('Reached max amount of characters');
      setName(name.slice(0, -1));
      return;
    }

    ctx.font = `${fontSize}px Dancing Script, cursive`;
    ctx.fillText(name, 2, textHeight / 2);

    setFontSize(Math.max(MIN_FONT_SIZE, fontSize));
  }, [ref, name, canvasWidth]);

  return (
    <Box ref={computeWidth}>
      <FormItem
        label={
          <FormLabel htmlFor="signature" display="flex" justifyContent="space-between">
            {translate('signSidebar.signature')}
            {handleReset && <Link onClick={handleReset}>{translate('actions.reset')}</Link>}
          </FormLabel>
        }
        warning={warning}
      >
        <Input
          id="signature"
          autoFocus
          style={{ height, minWidth: canvasWidth, fontSize }}
          className={styles['text-signature-input']}
          onChange={onNameChange}
          hasWarning={!!warning}
          value={name}
        />
      </FormItem>
      <canvas ref={ref} className={styles['text-signature-canvas']} width={canvasWidth} height={height} />
    </Box>
  );
};

export default TextSignature;
